import { Button } from '@/components/ui/button';
import { useUnarchiveProject } from '@/features/projects/hooks/useUnarchiveProject';
import { useWorkspaceRole } from '@/lib/hooks/useWorkspaceRole';
import { listArchivedProjectsByWorkspaceQuery } from '@/lib/supabase/queries/projects';
import { getWorkspaceBySlugQuery } from '@/lib/supabase/queries/workspaces';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useParams } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Archive, ArchiveRestore, ArrowLeft } from 'lucide-react';

export const Route = createFileRoute('/_app/$wsSlug/projects/archivados')({
  component: ArchivedProjectsPage,
});

function ArchivedProjectsPage() {
  const { wsSlug } = useParams({ from: '/_app/$wsSlug/projects/archivados' });
  const { canManage } = useWorkspaceRole(wsSlug);

  const wsQ = useQuery(getWorkspaceBySlugQuery(wsSlug));
  const projectsQ = useQuery({
    ...listArchivedProjectsByWorkspaceQuery(wsQ.data?.id ?? ''),
    enabled: !!wsQ.data?.id,
  });

  const unarchive = useUnarchiveProject();

  if (wsQ.isPending || (wsQ.data && projectsQ.isPending)) {
    return <div className="p-6 text-sm text-quiet">Cargando archivados...</div>;
  }
  if (!wsQ.data) {
    return <div className="p-6 text-sm text-quiet">Workspace no encontrado o sin permisos.</div>;
  }

  const projects = projectsQ.data ?? [];
  const workspaceId = wsQ.data.id;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Link
              to="/$wsSlug/projects"
              params={{ wsSlug }}
              className="text-quiet hover:text-ink"
              aria-label="Volver a proyectos"
            >
              <ArrowLeft size={18} />
            </Link>
            <h1 className="text-xl font-semibold text-ink">Proyectos archivados</h1>
          </div>
          <p className="mt-0.5 text-sm text-quiet">
            Proyectos que se sacaron de la lista activa. Restaurar los devuelve a la grilla.
          </p>
        </div>
      </div>

      {projects.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-surface p-10 text-center">
          <Archive size={28} className="mx-auto mb-2 text-mute" aria-hidden />
          <p className="text-sm text-quiet">No hay proyectos archivados en este workspace.</p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {projects.map((p) => {
            const archivedDate = p.archived_at
              ? format(parseISO(p.archived_at), 'd MMM yyyy', { locale: es })
              : '';
            return (
              <li
                key={p.id}
                className="flex items-center gap-3 rounded-lg border border-border bg-surface p-3"
              >
                <span
                  className="inline-block h-3 w-3 rounded-sm shrink-0"
                  style={{ backgroundColor: p.color ?? 'var(--accent)' }}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink truncate">{p.name}</p>
                  <p className="text-xs text-quiet">
                    <span className="identifier uppercase">{p.identifier}</span>
                    {archivedDate ? <span className="ml-2">archivado {archivedDate}</span> : null}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => unarchive.mutate({ projectId: p.id, workspaceId })}
                  disabled={!canManage || unarchive.isPending}
                  title={canManage ? 'Restaurar proyecto' : 'Solo owners y admins pueden restaurar'}
                >
                  <ArchiveRestore size={14} />
                  Restaurar
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
